/**
 * useDemoCraftPrint — React hook for the /mycraftz Active-CraftPrintz demo (C-2).
 *
 * Backing the shared cached template minted by /craftprint/demo-init on the
 * backend (Checkpoint C-1). Per-browser install_id is persisted in
 * localStorage so the day-0/3/7 reveal cadence + Phala/Vighna/Upaya edits
 * survive refreshes but never leak between browsers.
 *
 * localStorage shape:
 *   icraftz.demo.craftprint.<slug> = {
 *     install_id, installed_at (iso), lang
 *   }
 *
 * State returned by the hook:
 *   { loading, error, data: { install_id, craftprints[], installed_at, days_since_install }, patch }
 *
 * `patch(idea_id, fields)` calls PATCH /craftprint/{install_id}/{idea_id}
 * and refreshes local state on success. Fields: {phala?, vighna?, upaya?}.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install`;
const LS_PREFIX = "icraftz.demo.craftprint.";

function readCache(slug) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + slug);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeCache(slug, payload) {
  try { localStorage.setItem(LS_PREFIX + slug, JSON.stringify(payload)); }
  catch { /* localStorage full / disabled — degrade to no persistence */ }
}
function clearCache(slug) {
  try { localStorage.removeItem(LS_PREFIX + slug); } catch { /* noop */ }
}

export function useDemoCraftPrint(slug, lang = "en") {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Initialise: read cache, GET if present, else POST demo-init.
  // Uses AbortController so React Strict Mode's double-invocation cancels
  // the first request cleanly (no duplicate Mongo writes, no localStorage race).
  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      const cached = readCache(slug);
      // Fast path — cached install_id in this browser, GET it.
      if (cached?.install_id) {
        try {
          const r = await axios.get(`${API}/craftprints`, {
            params: {
              book_id: cached.install_id.split(":")[1],
              targetLanguage: cached.lang || lang,
              install_id: cached.install_id,
            },
            timeout: 30000,
            signal: controller.signal,
          });
          if (r.data?.found) {
            setData({
              install_id:         cached.install_id,
              installed_at:       cached.installed_at,
              craftprints:        r.data.craftprints || [],
              days_since_install: r.data.days_since_install || 0,
              book_id:            r.data.book_id,
              book_title:         r.data.book_title,
            });
            setLoading(false);
            return;
          }
          // Cached but GET says "not found" — TTL expired. Fall through to re-init.
          clearCache(slug);
        } catch (e) {
          if (axios.isCancel(e) || e?.name === "CanceledError") return;
          clearCache(slug);
        }
      }
      // Slow path — first visit or cache stale. Mint a fresh demo install.
      try {
        const r = await axios.post(`${API}/craftprint/demo-init`, {
          book_slug: slug,
          targetLanguage: lang,
        }, { timeout: 90000, signal: controller.signal });
        writeCache(slug, {
          install_id:   r.data.install_id,
          installed_at: r.data.installed_at,
          lang:         r.data.lang,
        });
        setData({
          install_id:         r.data.install_id,
          installed_at:       r.data.installed_at,
          craftprints:        r.data.craftprints || [],
          days_since_install: 0, // fresh init — day 0
          book_id:            r.data.book_id,
          book_title:         r.data.book_title,
        });
      } catch (e) {
        if (axios.isCancel(e) || e?.name === "CanceledError") return;
        setError(e?.response?.data?.detail || e?.message || "init failed");
      } finally {
        setLoading(false);
      }
    })();
    return () => { controller.abort(); };
  }, [slug, lang]);

  // Patch phala/vighna/upaya on a specific idea. Refreshes local craftprints array.
  const patch = useCallback(async (idea_id, fields) => {
    if (!data?.install_id) return false;
    try {
      const r = await axios.patch(
        `${API}/craftprint/${encodeURIComponent(data.install_id)}/${idea_id}`,
        fields,
        { timeout: 30000 },
      );
      if (r.data?.craftprints) {
        setData((d) => (d ? { ...d, craftprints: r.data.craftprints } : d));
      }
      return true;
    } catch (e) {
      return false;
    }
  }, [data]);

  return { loading, error, data, patch };
}

/** Tier revealed based on days_since_install. Mirrors the Sadhana cadence. */
export function revealTierForDays(days) {
  if (days >= 7) return 3;
  if (days >= 3) return 2;
  return 1;
}
